<template>
  <div class="curved-text-outer-layer" :style="computedExternalStyle">
    <div class="curved-text-inner-layer" :style="computedInternalStyle">
      <span v-if="enabled" class="curved-text-letter" :class="{invisible: calculating}" v-for="(letter, index) in letters" :style="styles[index]" ref="letterSpan">{{letter}}</span>
      <span v-if="!enabled">{{text}}</span>
    </div>
  </div>
</template>

<script>
import defer from 'lodash/defer'
import Vue from 'vue'
const radiansPerDegree = Math.PI / 180
const degreesPerRadian = 180 / Math.PI

export default {
  name: 'CurvedText',
  props: {
    enabled: {
      type: Boolean,
      requied: false,
      default: true
    },
    text: {
      type: String,
      required: true
    },
    radius: {
      type: Number,
      required: true
    },
    dir: {
      type: Number,
      required: false,
      default: 1
    },
    forceWidth: {
      type: Boolean,
      required: false,
      default: true
    },
    forceHeight: {
      type: Boolean,
      required: false,
      default: true
    },
    externalStyle: {
      type: Object,
      required: false,
      default: function () {
        return {}
      }
    },
    internalTransform: {
      type: String,
      required: false,
      // default: 'translateX(-50%)'
      default: 'translate3d(-50%, 0, 0)'
    }
  },

  data: function () {
    return {
      styles: [],
      calculating: true,
      height: 0,
      width: 0
    }
  },

  computed: {
    letters: function () {
      return this.text.trim().split('')
    },
    computedExternalStyle: function () {
      // return {}
      if (this.calculating) {
        return {}
      }
      return this.externalStyle
    },

    computedInternalStyle: function () {
      // return {}
      if (this.calculating) {
        return {}
      }
      return {
        height: this.height === 0 ? 'auto' : this.height,
        width: this.width === 0 ? 'auto' : this.width,
        position: 'absolute',
        transform: this.internalTransform
      }
    }
  },

  watch: {
    radius: function () {
      this.recalculateStyles()
    },
    dir: function () {
      this.recalculateStyles()
    },
    forceWidth: function () {
      this.recalculateStyles()
    },
    forceHeight: function () {
      this.recalculateStyles()
    },
    text: function () {
      this.calculateMetrics()
    }
  },

  mounted: function () {
    this.calculateMetrics()
    // this.calculating = false
  },

  methods: {
    calculateMetrics: function () {
      this.styles = []
      this.height = 0
      this.width = 0
      // this.internalStyle = {}
      this.calculating = true

      defer(() => {
        if (!this.$refs.letterSpan) {
          this.calculating = false
          return
        }
        const {fontSize, lineHeight} = window.getComputedStyle(this.$el)
        this._fontSize = parseFloat(fontSize)
        this._lineHeight = parseFloat(lineHeight) || this._fontSize
        this._metrics = this.$refs.letterSpan.map(this.getRect)
        const totalWidth = this._metrics.reduce((sum, { width }) => sum + width, 0)
        this._minRadius = (totalWidth / Math.PI / 2) + this._lineHeight

        this.recalculateStyles()
        this.calculating = false
      })
    },
    recalculateStyles: function () {
      if (this._metrics && this._metrics.length > 0) {
        this.styles = []
        // this.internalStyle = {}

        const { radius, dir } = this
        const originY = dir === -1 ? (-radius + this._lineHeight) : radius
        const transformOrigin = `center ${originY / this._fontSize}em`
        const innerRadius = radius - this._lineHeight
        const { rotations, θ } = this.getLetterRotations(this._metrics, innerRadius)

        this.$refs.letterSpan.forEach((letter, index) => {
          // const { style } = letter
          const rotate = ((θ * -0.5) + rotations[index]) * dir
          const translateX = (this._metrics[index].width * -0.5) / this._fontSize
          const transform = `translateX(${translateX}em) rotate(${rotate}deg)`
          // const transform = `translate3d(${translateX}em, 0, 0) rotate3d(0, 0, 1, ${rotate}deg)`
          // const transform = `o-translate3d(${translateX}em, 0, 0) rotate3d(0, 0, 1, ${rotate}deg)`

          Vue.set(this.styles, index, {
            position: 'absolute',
            // top: `-${this._fontSize / 2}px`,
            bottom: dir === -1 ? 0 : 'auto',
            left: '50%',
            transform,
            transformOrigin
          })
        })

        if (this.forceHeight) {
          const height = θ > 180 ? this.sagitta(radius, θ) : this.sagitta(innerRadius, θ) + this._lineHeight
          this.height = `${height / this._fontSize}em`
        }

        if (this.forceWidth) {
          const width = this.chord(radius, Math.min(180, θ))
          this.width = `${width / this._fontSize}em`
        }
      }
    },
    splitNode: function (node, wrapper = 'span') {
      const wrapperElement = document.createElement(wrapper)

      return node.innerText.trim().split('').map(char => {
        const parent = wrapperElement.cloneNode()
        parent.insertAdjacentHTML('afterbegin', char === ' ' ? '&nbsp;' : char)
        return parent
      })
    },
    sagitta: function (r, θ) {
      return r * (1 - Math.cos(this.degreesToRadians(θ / 2)))
    },
    getRect: function (element) {
      const rect = element.getBoundingClientRect()
      return {
        height: rect.height,
        left: rect.left + window.pageXOffset,
        top: rect.top + window.pageYOffset,
        width: rect.width
      }
    },
    getLetterRotations: function (metrics, r) {
      return metrics.reduce((data, { width }) => {
        const rotation = this.radiansToDegrees(width / r)

        return {
          θ: data.θ + rotation,
          rotations: data.rotations.concat([ data.θ + (rotation / 2) ])
        }
      }, { θ: 0, rotations: [] })
    },
    degreesToRadians: function (angleInDegrees) {
      return angleInDegrees * radiansPerDegree
    },
    radiansToDegrees: function (angleInRadians) {
      return angleInRadians * degreesPerRadian
    },
    chord: function (r, θ) {
      return 2 * r * Math.sin(this.degreesToRadians(θ / 2))
    }
  }
}
</script>


<style lang="scss">


.curved-text-outer-layer {
  will-change: transform;
  &.hidden {
    .letter {
      display: none;
    }
  }
}

.curved-text-inner-layer {
  // background-color: #f00;
  padding: 20px;
  // background: #0f0;
  white-space: nowrap;
}

.curved-text-letter {
  display: inline;
  pointer-events: none;

  // color: inherit;

  &.invisible {
    color: transparent !important;
  }
}

</style>



// WEBPACK FOOTER //
// src/atoms/CurvedText.vue